import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import React from 'react';

import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { formatPriceCents, getProduct } from '../subscriptions/catalog';
import {
  meetsProductRequirements,
  normalizeEntitlementsMap,
  ownsProduct,
} from '../subscriptions/entitlements';
import { resolveCheckoutReturnTo } from '../subscriptions/purchaseSteps';
import type { EntitlementsMap, ProductId } from '../subscriptions/types';

export interface SubscriptionCatalogItemProps {
  productId: ProductId;
  assignmentId?: number;
  entitlements?: EntitlementsMap | null;
  returnTo?: string;
  disabled?: boolean;
  /** Hide product title when rendered inside a stepper StepLabel. */
  hideTitle?: boolean;
  /** Highlight as the recommended next purchase. */
  emphasized?: boolean;
  /** Dense single-row layout for stepper steps. */
  compact?: boolean;
}

const SubscriptionCatalogItem: React.FC<SubscriptionCatalogItemProps> = ({
  productId,
  assignmentId,
  entitlements,
  returnTo,
  disabled = false,
  hideTitle = false,
  emphasized = false,
  compact = false,
}) => {
  const product = getProduct(productId);
  const { loading, error, clearError, startCheckout } = useSubscriptionCheckout();
  const normalized = normalizeEntitlementsMap(entitlements);
  const owned = ownsProduct(normalized, productId);
  // Prerequisites are a UI affordance: the server fulfills any product it is
  // handed, so this only steers buyers away from tiers they can't use yet.
  const locked = !owned && !meetsProductRequirements(normalized, productId);

  if (!product) {
    return null;
  }

  const canPurchase = !owned && !locked && !disabled;

  const handleBuy = async () => {
    clearError();
    // Decide the post-checkout destination from pre-purchase state: entry
    // purchases go to onboarding, upgrades to their per-product target. A
    // brand-new assignment resolves to undefined and is routed to onboarding by
    // the subscriptions-page confirm handler once its id is known.
    const resolvedReturnTo = resolveCheckoutReturnTo({
      entitlements: normalized,
      productId,
      assignmentId,
    });
    const effectiveReturnTo = resolvedReturnTo ?? returnTo;
    await startCheckout({
      ...(assignmentId !== undefined ? { assignmentId } : {}),
      productId,
      ...(effectiveReturnTo ? { returnTo: effectiveReturnTo } : {}),
    });
  };

  const summary = product.description;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: compact ? 'row' : { xs: 'column', sm: 'row' },
        alignItems: compact ? 'center' : { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: compact ? 1 : 2,
        p: compact ? (emphasized ? 0.75 : 0.25) : 2,
        borderRadius: compact ? 1 : 2,
        border: compact ? 0 : emphasized ? 2 : 1,
        borderColor: emphasized ? 'secondary.main' : 'divider',
        bgcolor: compact
          ? emphasized
            ? 'action.hover'
            : 'transparent'
          : emphasized
            ? 'action.hover'
            : 'background.paper',
      }}
      data-testid={
        owned
          ? `subscription-catalog-owned--${productId}`
          : locked
            ? `subscription-catalog-locked--${productId}`
            : `subscription-catalog-item--${productId}`
      }
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!hideTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1">{product.name}</Typography>
            {owned && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Purchased"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        )}
        {hideTitle && owned && !compact && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Purchased"
            size="small"
            color="success"
            variant="outlined"
            sx={{ mb: 0.5 }}
          />
        )}
        <Typography
          variant={compact ? 'caption' : 'body2'}
          color="text.secondary"
          sx={compact ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35 } : undefined}
        >
          {summary}
        </Typography>
        {error && (
          <Alert
            severity="error"
            sx={{ mt: compact ? 0.75 : 1.5 }}
            action={
              error.retryable ? (
                <Button color="inherit" size="small" onClick={() => void handleBuy()} disabled={loading}>
                  Try again
                </Button>
              ) : undefined
            }
          >
            <Typography variant="body2">{error.message}</Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {error.instruction}
            </Typography>
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          alignItems: 'center',
          gap: compact ? 1 : 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant={compact ? 'subtitle2' : 'h6'}
          color="secondary.main"
          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          {formatPriceCents(product.price_cents)}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size={compact ? 'small' : 'medium'}
          disabled={!canPurchase || loading}
          onClick={() => void handleBuy()}
          data-attr={`subscription-catalog_buy--${productId}`}
          data-testid={`subscription-catalog-buy--${productId}`}
          sx={compact ? { minWidth: 72, px: 1.5, py: 0.25, fontSize: '0.75rem' } : undefined}
        >
          {loading ? '…' : owned ? 'Owned' : locked ? 'Locked' : 'Level Up'}
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionCatalogItem;
